import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  FileText,
  Download,
  Users,
  Calendar,
  Loader2,
  CheckCircle,
  Clock,
  CreditCard,
  Plus,
  Eye,
  TrendingUp,
  TrendingDown,
  Banknote,
  AlertCircle,
  RefreshCw,
  CheckSquare,
  Square,
  Edit,
  RotateCcw,
  Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import jsPDF from 'jspdf';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PayrollManagementPage = () => {
  const { user } = useAuth();
  const { formatCurrency, currency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [slips, setSlips] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Dialog states
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [slipDialogOpen, setSlipDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Employee selection for payroll processing
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  
  // Edit salary slip state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSlip, setEditingSlip] = useState(null);
  const [adjustments, setAdjustments] = useState({
    additional_earnings: 0,
    additional_deductions: 0,
    adjustment_reason: ''
  });
  
  // Advance form
  const [advanceForm, setAdvanceForm] = useState({
    user_id: '',
    amount: '',
    monthly_deduction: '',
    reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [slipsRes, advancesRes, employeesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/hr/payroll/slips?month=${selectedMonth}`, { headers }),
        axios.get(`${API_BASE_URL}/hr/advances`, { headers }),
        axios.get(`${API_BASE_URL}/hr/employees`, { headers })
      ]);
      
      setSlips(slipsRes.data.slips);
      setAdvances(advancesRes.data.advances);
      setEmployees(employeesRes.data.employees);
      
      // Calculate summary
      if (slipsRes.data.slips.length > 0) {
        const total = slipsRes.data.slips.reduce((sum, s) => sum + s.net_salary, 0);
        const totalEarnings = slipsRes.data.slips.reduce((sum, s) => sum + s.total_earnings, 0);
        const totalDeductions = slipsRes.data.slips.reduce((sum, s) => sum + s.total_deductions, 0);
        setPayrollSummary({
          count: slipsRes.data.slips.length,
          total_net: total,
          total_earnings: totalEarnings,
          total_deductions: totalDeductions
        });
      } else {
        setPayrollSummary(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch eligible employees when opening process dialog
  const fetchEligibleEmployees = async () => {
    setLoadingEligible(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/hr/payroll/eligible-employees?month=${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEligibleEmployees(response.data);
      // Pre-select all eligible employees
      setSelectedEmployees(response.data.eligible.map(e => e.id));
      setProcessResult(null);
    } catch (error) {
      console.error('Error fetching eligible employees:', error);
      toast.error('Failed to load eligible employees');
    } finally {
      setLoadingEligible(false);
    }
  };

  const openProcessDialog = () => {
    setProcessDialogOpen(true);
    fetchEligibleEmployees();
  };

  const toggleEmployeeSelection = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedEmployees.length === eligibleEmployees.eligible?.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(eligibleEmployees.eligible?.map(e => e.id) || []);
    }
  };

  const handleProcessPayroll = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/hr/payroll/process`,
        { 
          month: selectedMonth,
          user_ids: selectedEmployees
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProcessResult(response.data);
      
      // Show appropriate message
      const { summary, failed } = response.data;
      if (summary.failed > 0 && summary.processed === 0) {
        // All failed - show the first error
        const firstError = failed?.[0]?.error || 'Unknown error';
        toast.error(`All ${summary.failed} employees failed: ${firstError}`);
      } else if (summary.failed > 0) {
        toast.warning(`Processed ${summary.processed} employees, ${summary.failed} failed`);
      } else {
        toast.success(response.data.message);
      }
      
      fetchData();
      // Refresh eligible employees list
      fetchEligibleEmployees();
    } catch (error) {
      console.error('Error processing payroll:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const retryFailedEmployee = async (empId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/hr/payroll/process`,
        { 
          month: selectedMonth,
          user_ids: [empId]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.summary.processed > 0) {
        toast.success('Employee payroll processed successfully');
        fetchData();
        fetchEligibleEmployees();
      } else if (response.data.failed?.length > 0) {
        toast.error(`Failed: ${response.data.failed[0].error}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateAdvance = async () => {
    if (!advanceForm.user_id || !advanceForm.amount || !advanceForm.monthly_deduction) {
      toast.error('Please fill all required fields');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/advance?creator_id=${user.id}`,
        {
          ...advanceForm,
          amount: parseFloat(advanceForm.amount),
          monthly_deduction: parseFloat(advanceForm.monthly_deduction)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Advance issued successfully');
      setAdvanceDialogOpen(false);
      setAdvanceForm({ user_id: '', amount: '', monthly_deduction: '', reason: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating advance:', error);
      toast.error('Failed to issue advance');
    } finally {
      setProcessing(false);
    }
  };

  // Edit/Adjust salary slip
  const openEditDialog = (slip) => {
    setEditingSlip(slip);
    setAdjustments({
      additional_earnings: 0,
      additional_deductions: 0,
      adjustment_reason: ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveAdjustments = async () => {
    if (!adjustments.adjustment_reason.trim()) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/payroll/slips/${editingSlip.id}/adjust`,
        {
          additional_earnings: parseFloat(adjustments.additional_earnings) || 0,
          additional_deductions: parseFloat(adjustments.additional_deductions) || 0,
          adjustment_reason: adjustments.adjustment_reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Salary slip adjusted successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust salary slip');
    } finally {
      setProcessing(false);
    }
  };

  const handleRegenerateSlip = async (slipId) => {
    if (!confirm('This will delete the current slip and regenerate it. Are you sure?')) {
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/payroll/slips/${slipId}/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Salary slip regenerated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate salary slip');
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalizeSlip = async (slipId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/payroll/slips/${slipId}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Salary slip finalized');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize salary slip');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewSlip = (slip) => {
    setSelectedSlip(slip);
    setSlipDialogOpen(true);
  };

  const generatePDF = (slip) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(212, 175, 55);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('WOODEN TWIST', pageWidth / 2, 18, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Salary Slip', pageWidth / 2, 28, { align: 'center' });
    doc.text(slip.slip_number, pageWidth / 2, 35, { align: 'center' });
    
    // Employee Info
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(11);
    let y = 55;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Employee:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(slip.user_name, 60, y);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Month:', 120, y);
    doc.setFont('helvetica', 'normal');
    doc.text(slip.month, 145, y);
    
    // Divider
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, pageWidth - 20, y);
    
    // Earnings Section
    y += 15;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, y - 5, pageWidth - 40, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('EARNINGS', 25, y + 2);
    
    y += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const earnings = [
      ['Base Salary', slip.base_salary],
      ['Housing Allowance', slip.housing_allowance],
      ['Transport Allowance', slip.transport_allowance],
      ['Food Allowance', slip.food_allowance],
      ['Other Allowances', slip.other_allowances],
      ['Overtime Pay', slip.overtime_pay],
      ['Incentives', slip.incentives]
    ];
    
    earnings.forEach(([label, value]) => {
      if (value > 0) {
        doc.text(label, 25, y);
        doc.text(`${currency} ${value.toLocaleString()}`, pageWidth - 25, y, { align: 'right' });
        y += 8;
      }
    });
    
    doc.setFont('helvetica', 'bold');
    doc.text('Total Earnings', 25, y);
    doc.text(`${currency} ${slip.total_earnings.toLocaleString()}`, pageWidth - 25, y, { align: 'right' });
    
    // Deductions Section
    y += 15;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, y - 5, pageWidth - 40, 10, 'F');
    doc.setFontSize(12);
    doc.text('DEDUCTIONS', 25, y + 2);
    
    y += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const deductions = [
      ['Penalties', slip.penalties],
      ['Leave Deductions', slip.leave_deductions],
      ['Undertime Deduction', slip.undertime_deduction || 0],
      ['Advance Deduction', slip.advance_deduction],
      ['Loan Deduction', slip.loan_deduction],
      ['Other Deductions', slip.other_deductions]
    ];
    
    deductions.forEach(([label, value]) => {
      if (value > 0) {
        doc.text(label, 25, y);
        doc.text(`${currency} ${value.toLocaleString()}`, pageWidth - 25, y, { align: 'right' });
        y += 8;
      }
    });
    
    doc.setFont('helvetica', 'bold');
    doc.text('Total Deductions', 25, y);
    doc.text(`${currency} ${slip.total_deductions.toLocaleString()}`, pageWidth - 25, y, { align: 'right' });
    
    // Net Salary
    y += 20;
    doc.setFillColor(212, 175, 55);
    doc.rect(20, y - 8, pageWidth - 40, 18, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text('NET SALARY', 25, y + 3);
    doc.text(`${currency} ${slip.net_salary.toLocaleString()}`, pageWidth - 25, y + 3, { align: 'right' });
    
    // Attendance Summary
    y += 30;
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Attendance Summary', 25, y);
    
    y += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Working Days: ${slip.working_days}`, 25, y);
    doc.text(`Present: ${slip.present_days}`, 70, y);
    doc.text(`Late: ${slip.late_days}`, 105, y);
    doc.text(`Leave: ${slip.leave_days}`, 135, y);
    doc.text(`Absent: ${slip.absent_days}`, 165, y);
    
    // Footer
    y = 270;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('This is a computer-generated document. No signature required.', pageWidth / 2, y, { align: 'center' });
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, y + 5, { align: 'center' });
    
    // Save
    doc.save(`Salary_Slip_${slip.slip_number}.pdf`);
    toast.success('PDF downloaded');
  };

  const StatusBadge = ({ status }) => {
    const config = {
      draft: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Draft' },
      adjusted: { color: 'bg-orange-500/20 text-orange-400', label: 'Adjusted' },
      finalized: { color: 'bg-green-500/20 text-green-400', label: 'Finalized' },
      paid: { color: 'bg-blue-500/20 text-blue-400', label: 'Paid' }
    };
    const { color, label } = config[status] || config.draft;
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  // Generate month options
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Payroll Management</h1>
          <p className="text-wt-text-muted">Process salaries and manage advances</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48 bg-wt-surface border-wt-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setAdvanceDialogOpen(true)}
            className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
          >
            <Banknote className="h-4 w-4 mr-2" />
            Issue Advance
          </Button>
          <Button
            onClick={openProcessDialog}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Process Payroll
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {payrollSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-wt-gold/20">
                  <Users className="h-5 w-5 text-wt-gold" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-wt-text-primary">{payrollSummary.count}</p>
                  <p className="text-xs text-wt-text-muted">Employees Processed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">
                    {payrollSummary.total_earnings.toLocaleString()}
                  </p>
                  <p className="text-xs text-wt-text-muted">Total Earnings (AED)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <TrendingDown className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">
                    {payrollSummary.total_deductions.toLocaleString()}
                  </p>
                  <p className="text-xs text-wt-text-muted">Total Deductions (AED)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <DollarSign className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-400">
                    {payrollSummary.total_net.toLocaleString()}
                  </p>
                  <p className="text-xs text-wt-text-muted">Total Net Salary (AED)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="slips" className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="slips" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            Salary Slips ({slips.length})
          </TabsTrigger>
          <TabsTrigger value="advances" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            Active Advances ({advances.length})
          </TabsTrigger>
        </TabsList>

        {/* Salary Slips Tab */}
        <TabsContent value="slips" className="mt-4">
          {slips.length === 0 ? (
            <Card className="bg-wt-surface border-wt-border">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
                <p className="text-wt-text-muted">No salary slips for this month</p>
                <Button
                  onClick={openProcessDialog}
                  className="mt-4 bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  Process Payroll
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {slips.map((slip) => (
                <motion.div
                  key={slip.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-wt-surface border-wt-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-wt-gold/20 flex items-center justify-center">
                            <span className="text-lg font-bold text-wt-gold">
                              {slip.user_name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-wt-text-primary">{slip.user_name}</p>
                            <p className="text-xs text-wt-text-muted">{slip.slip_number}</p>
                          </div>
                        </div>
                        <div className="hidden md:flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-wt-text-muted">Earnings</p>
                            <p className="text-sm font-medium text-green-400">
                              +{slip.total_earnings.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-wt-text-muted">Deductions</p>
                            <p className="text-sm font-medium text-red-400">
                              -{slip.total_deductions.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-wt-text-muted">Net Salary</p>
                            <p className="text-lg font-bold text-wt-gold">
                              {slip.net_salary.toLocaleString()} AED
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={slip.status} />
                          {slip.status !== 'finalized' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(slip)}
                                className="border-wt-border"
                                title="Adjust"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRegenerateSlip(slip.id)}
                                className="border-wt-border"
                                title="Regenerate"
                                disabled={processing}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleFinalizeSlip(slip.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                title="Finalize"
                                disabled={processing}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewSlip(slip)}
                            className="border-wt-border"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => generatePDF(slip)}
                            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Advances Tab */}
        <TabsContent value="advances" className="mt-4">
          {advances.length === 0 ? (
            <Card className="bg-wt-surface border-wt-border">
              <CardContent className="py-12 text-center">
                <Banknote className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
                <p className="text-wt-text-muted">No active advances</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {advances.map((advance) => (
                <Card key={advance.id} className="bg-wt-surface border-wt-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-wt-text-primary">{advance.user_name}</p>
                        <p className="text-sm text-wt-text-muted">{advance.description}</p>
                        <p className="text-xs text-wt-text-muted mt-1">
                          Monthly deduction: {advance.monthly_deduction} AED
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-orange-400">
                          {advance.remaining_balance.toLocaleString()} AED
                        </p>
                        <p className="text-xs text-wt-text-muted">Remaining Balance</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Process Payroll Dialog with Employee Selection */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Process Payroll - {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</DialogTitle>
          </DialogHeader>
          
          {loadingEligible ? (
            <div className="py-12 flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-wt-gold mb-4" />
              <p className="text-wt-text-muted">Loading employees...</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Summary Cards */}
              {eligibleEmployees.summary && (
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                    <p className="text-2xl font-bold text-green-400">{eligibleEmployees.summary.eligible_count}</p>
                    <p className="text-xs text-green-400">Eligible</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-center">
                    <p className="text-2xl font-bold text-blue-400">{eligibleEmployees.summary.already_processed_count}</p>
                    <p className="text-xs text-blue-400">Already Processed</p>
                  </div>
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{eligibleEmployees.summary.not_configured_count}</p>
                    <p className="text-xs text-yellow-400">Not Configured</p>
                  </div>
                  <div className="p-3 rounded-lg bg-wt-bg border border-wt-border text-center">
                    <p className="text-2xl font-bold text-wt-text-primary">{eligibleEmployees.summary.total_active}</p>
                    <p className="text-xs text-wt-text-muted">Total Active</p>
                  </div>
                </div>
              )}

              {/* Eligible Employees Selection */}
              {eligibleEmployees.eligible?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-wt-text-primary font-medium">Select Employees to Process</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleSelectAll}
                      className="text-wt-gold hover:text-wt-gold-hover"
                    >
                      {selectedEmployees.length === eligibleEmployees.eligible.length ? (
                        <><CheckSquare className="h-4 w-4 mr-1" /> Deselect All</>
                      ) : (
                        <><Square className="h-4 w-4 mr-1" /> Select All ({eligibleEmployees.eligible.length})</>
                      )}
                    </Button>
                  </div>
                  <div className="border border-wt-border rounded-lg max-h-48 overflow-y-auto">
                    {eligibleEmployees.eligible.map((emp) => (
                      <div 
                        key={emp.id}
                        className={`flex items-center justify-between p-3 border-b border-wt-border last:border-b-0 cursor-pointer hover:bg-wt-bg ${selectedEmployees.includes(emp.id) ? 'bg-wt-gold/10' : ''}`}
                        onClick={() => toggleEmployeeSelection(emp.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={selectedEmployees.includes(emp.id)}
                            onCheckedChange={() => toggleEmployeeSelection(emp.id)}
                            className="data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
                          />
                          <div>
                            <p className="text-sm font-medium text-wt-text-primary">{emp.name}</p>
                            <p className="text-xs text-wt-text-muted">{emp.department || 'No Department'} • {emp.designation || 'No Designation'}</p>
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-wt-gold">
                          {formatCurrency(emp.monthly_salary)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Already Processed */}
              {eligibleEmployees.already_processed?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-wt-text-muted text-sm">Already Processed ({eligibleEmployees.already_processed.length})</Label>
                  <div className="border border-wt-border rounded-lg bg-blue-500/5 max-h-32 overflow-y-auto">
                    {eligibleEmployees.already_processed.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-2 border-b border-wt-border/50 last:border-b-0">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-400" />
                          <span className="text-sm text-wt-text-muted">{emp.name}</span>
                        </div>
                        <span className="text-xs text-blue-400">{emp.slip_number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Not Configured */}
              {eligibleEmployees.not_configured?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-yellow-400 text-sm">Not Configured - Salary Missing ({eligibleEmployees.not_configured.length})</Label>
                  <div className="border border-yellow-500/30 rounded-lg bg-yellow-500/5 max-h-32 overflow-y-auto">
                    {eligibleEmployees.not_configured.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-2 border-b border-yellow-500/20 last:border-b-0">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-yellow-400" />
                          <span className="text-sm text-wt-text-muted">{emp.name}</span>
                        </div>
                        <span className="text-xs text-yellow-400">{emp.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Processing Result - Failed Employees */}
              {processResult?.failed?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-red-400 text-sm">Failed to Process ({processResult.failed.length})</Label>
                  <div className="border border-red-500/30 rounded-lg bg-red-500/5">
                    {processResult.failed.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-3 border-b border-red-500/20 last:border-b-0">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-red-400" />
                            <span className="text-sm font-medium text-wt-text-primary">{emp.name}</span>
                          </div>
                          <p className="text-xs text-red-400 mt-1 ml-6">{emp.error}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => retryFailedEmployee(emp.id)}
                          disabled={processing}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Retry
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No eligible employees */}
              {eligibleEmployees.eligible?.length === 0 && !loadingEligible && (
                <div className="py-8 text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
                  <p className="text-wt-text-muted">No eligible employees for payroll processing</p>
                  <p className="text-xs text-wt-text-muted mt-2">All employees may have already been processed or don't have salaries configured.</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t border-wt-border pt-4 mt-4">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-wt-text-muted">
                {selectedEmployees.length} of {eligibleEmployees.eligible?.length || 0} selected
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setProcessDialogOpen(false);
                    setProcessResult(null);
                  }}
                  className="border-wt-border"
                >
                  Close
                </Button>
                <Button
                  onClick={handleProcessPayroll}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  disabled={processing || selectedEmployees.length === 0}
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <DollarSign className="h-4 w-4 mr-2" />
                      Process {selectedEmployees.length} Employee{selectedEmployees.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Advance Dialog */}
      <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Issue Advance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select
                value={advanceForm.user_id}
                onValueChange={(v) => setAdvanceForm({...advanceForm, user_id: v})}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border max-h-60">
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Advance Amount (AED)</Label>
              <Input
                type="number"
                value={advanceForm.amount}
                onChange={(e) => setAdvanceForm({...advanceForm, amount: e.target.value})}
                placeholder="2000"
                className="bg-wt-bg border-wt-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Monthly Deduction (AED)</Label>
              <Input
                type="number"
                value={advanceForm.monthly_deduction}
                onChange={(e) => setAdvanceForm({...advanceForm, monthly_deduction: e.target.value})}
                placeholder="500"
                className="bg-wt-bg border-wt-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={advanceForm.reason}
                onChange={(e) => setAdvanceForm({...advanceForm, reason: e.target.value})}
                placeholder="Reason for advance..."
                className="bg-wt-bg border-wt-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAdvanceDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdvance}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={processing}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Issue Advance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Salary Slip Dialog */}
      <Dialog open={slipDialogOpen} onOpenChange={setSlipDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              Salary Slip - {selectedSlip?.slip_number}
            </DialogTitle>
          </DialogHeader>
          {selectedSlip && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-wt-bg border border-wt-border">
                <div>
                  <p className="text-lg font-bold text-wt-text-primary">{selectedSlip.user_name}</p>
                  <p className="text-sm text-wt-text-muted">{selectedSlip.month}</p>
                </div>
                <StatusBadge status={selectedSlip.status} />
              </div>

              {/* Earnings & Deductions */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="font-medium text-green-400">Earnings</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Base Salary</span><span>{selectedSlip.base_salary}</span></div>
                    <div className="flex justify-between"><span>Housing</span><span>{selectedSlip.housing_allowance}</span></div>
                    <div className="flex justify-between"><span>Transport</span><span>{selectedSlip.transport_allowance}</span></div>
                    <div className="flex justify-between"><span>Food</span><span>{selectedSlip.food_allowance}</span></div>
                    <div className="flex justify-between"><span>Other</span><span>{selectedSlip.other_allowances}</span></div>
                    <div className="flex justify-between"><span>Overtime ({selectedSlip.overtime_hours}h)</span><span>{selectedSlip.overtime_pay}</span></div>
                    <div className="flex justify-between"><span>Incentives</span><span>{selectedSlip.incentives}</span></div>
                    <div className="flex justify-between font-bold border-t border-wt-border pt-1 mt-2">
                      <span>Total</span><span className="text-green-400">{selectedSlip.total_earnings}</span>
                    </div>
                  </div>
                  
                  {/* Incentive Details */}
                  {selectedSlip.incentive_details?.length > 0 && (
                    <div className="mt-3 p-2 bg-green-500/10 rounded border border-green-500/20">
                      <p className="text-xs font-medium text-green-400 mb-1">Incentive Breakdown</p>
                      {selectedSlip.incentive_details.map((inc, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-wt-text-muted">{inc.description} ({inc.date})</span>
                          <span className="text-green-400">+{inc.amount}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium text-red-400">Deductions</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Penalties</span><span>{selectedSlip.penalties}</span></div>
                    <div className="flex justify-between"><span>Leave Deductions</span><span>{selectedSlip.leave_deductions}</span></div>
                    <div className="flex justify-between"><span>Undertime ({selectedSlip.undertime_hours || 0}h)</span><span>{selectedSlip.undertime_deduction || 0}</span></div>
                    <div className="flex justify-between"><span>Advance</span><span>{selectedSlip.advance_deduction}</span></div>
                    <div className="flex justify-between"><span>Loan</span><span>{selectedSlip.loan_deduction || 0}</span></div>
                    <div className="flex justify-between font-bold border-t border-wt-border pt-1 mt-2">
                      <span>Total</span><span className="text-red-400">{selectedSlip.total_deductions}</span>
                    </div>
                  </div>
                  
                  {/* Penalty Details */}
                  {selectedSlip.penalty_details?.length > 0 && (
                    <div className="mt-3 p-2 bg-red-500/10 rounded border border-red-500/20">
                      <p className="text-xs font-medium text-red-400 mb-1">Penalty Breakdown</p>
                      {selectedSlip.penalty_details.map((pen, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-wt-text-muted">{pen.description} ({pen.date})</span>
                          <span className="text-red-400">-{pen.amount}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Advance Details */}
                  {selectedSlip.advance_details?.length > 0 && (
                    <div className="mt-3 p-2 bg-orange-500/10 rounded border border-orange-500/20">
                      <p className="text-xs font-medium text-orange-400 mb-1">Advance Deduction</p>
                      {selectedSlip.advance_details.map((adv, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-wt-text-muted">{adv.description}</span>
                          <span className="text-orange-400">-{adv.deducted_amount} (Rem: {adv.remaining_after})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Loan Details */}
                  {selectedSlip.loan_details?.length > 0 && (
                    <div className="mt-3 p-2 bg-purple-500/10 rounded border border-purple-500/20">
                      <p className="text-xs font-medium text-purple-400 mb-1">Loan Deduction</p>
                      {selectedSlip.loan_details.map((loan, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-wt-text-muted">{loan.description}</span>
                          <span className="text-purple-400">-{loan.deducted_amount} (Rem: {loan.remaining_after})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Net Salary */}
              <div className="p-4 rounded-lg bg-wt-gold/20 border border-wt-gold/30 text-center">
                <p className="text-sm text-wt-text-muted">Net Salary</p>
                <p className="text-3xl font-bold text-wt-gold">{selectedSlip.net_salary.toLocaleString()} AED</p>
              </div>

              {/* Attendance Summary */}
              <div className="grid grid-cols-5 gap-2 text-center">
                <div className="p-2 rounded bg-wt-bg">
                  <p className="text-lg font-bold text-wt-text-primary">{selectedSlip.working_days}</p>
                  <p className="text-xs text-wt-text-muted">Working</p>
                </div>
                <div className="p-2 rounded bg-wt-bg">
                  <p className="text-lg font-bold text-green-400">{selectedSlip.present_days}</p>
                  <p className="text-xs text-wt-text-muted">Present</p>
                </div>
                <div className="p-2 rounded bg-wt-bg">
                  <p className="text-lg font-bold text-yellow-400">{selectedSlip.late_days}</p>
                  <p className="text-xs text-wt-text-muted">Late</p>
                </div>
                <div className="p-2 rounded bg-wt-bg">
                  <p className="text-lg font-bold text-blue-400">{selectedSlip.leave_days}</p>
                  <p className="text-xs text-wt-text-muted">Leave</p>
                </div>
                <div className="p-2 rounded bg-wt-bg">
                  <p className="text-lg font-bold text-red-400">{selectedSlip.absent_days}</p>
                  <p className="text-xs text-wt-text-muted">Absent</p>
                </div>
              </div>
              
              {/* Day-wise Attendance Details */}
              {selectedSlip.attendance_details?.length > 0 && (
                <div className="border border-wt-border rounded-lg overflow-hidden">
                  <div className="bg-wt-bg px-3 py-2 border-b border-wt-border">
                    <h3 className="text-sm font-medium text-wt-text-primary">Day-wise Attendance</h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-wt-bg sticky top-0">
                        <tr className="text-wt-text-muted">
                          <th className="text-left p-2">Date</th>
                          <th className="text-center p-2">Status</th>
                          <th className="text-center p-2">Shift Hours</th>
                          <th className="text-center p-2">Actual Hours</th>
                          <th className="text-center p-2">Overtime</th>
                          <th className="text-center p-2">Undertime</th>
                          <th className="text-center p-2">Check In</th>
                          <th className="text-center p-2">Check Out</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSlip.attendance_details.map((att, i) => (
                          <tr key={i} className={`border-t border-wt-border/50 hover:bg-wt-bg/50 ${att.incomplete ? 'bg-orange-500/5' : ''}`}>
                            <td className="p-2 text-wt-text-primary">{att.date}</td>
                            <td className="p-2 text-center">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                att.status === 'on_time' || att.status === 'present' ? 'bg-green-500/20 text-green-400' :
                                att.status === 'late' ? 'bg-yellow-500/20 text-yellow-400' :
                                att.status === 'half_day' ? 'bg-orange-500/20 text-orange-400' :
                                att.status === 'absent' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {att.status}
                              </span>
                            </td>
                            <td className="p-2 text-center text-wt-text-muted">{att.shift_hours}h</td>
                            <td className="p-2 text-center font-medium">
                              {att.incomplete ? (
                                <span className="text-orange-400" title="Missing check-out">--</span>
                              ) : att.actual_hours !== null ? (
                                <span className="text-wt-text-primary">{att.actual_hours}h</span>
                              ) : (
                                <span className="text-wt-text-muted">0h</span>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              {att.overtime > 0 && <span className="text-blue-400">+{att.overtime}h</span>}
                            </td>
                            <td className="p-2 text-center">
                              {att.undertime > 0 && <span className="text-red-400">-{att.undertime}h</span>}
                            </td>
                            <td className="p-2 text-center text-wt-text-muted">
                              {att.check_in ? att.check_in.split('T')[1]?.substring(0, 5) || att.check_in : '-'}
                            </td>
                            <td className="p-2 text-center">
                              {att.check_out ? (
                                <span className="text-wt-text-muted">{att.check_out.split('T')[1]?.substring(0, 5) || att.check_out}</span>
                              ) : att.check_in ? (
                                <span className="text-orange-400" title="Missing check-out">Missing</span>
                              ) : (
                                <span className="text-wt-text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSlipDialogOpen(false)}
              className="border-wt-border"
            >
              Close
            </Button>
            <Button
              onClick={() => generatePDF(selectedSlip)}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Adjust Salary Slip Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Adjust Salary Slip</DialogTitle>
          </DialogHeader>
          {editingSlip && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-wt-bg border border-wt-border">
                <p className="font-medium text-wt-text-primary">{editingSlip.user_name}</p>
                <p className="text-sm text-wt-text-muted">{editingSlip.slip_number} • {editingSlip.month}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-wt-text-muted">Current Earnings:</span>
                  <span className="ml-2 text-green-400">{editingSlip.total_earnings?.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-wt-text-muted">Current Deductions:</span>
                  <span className="ml-2 text-red-400">{editingSlip.total_deductions?.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-wt-text-muted">Additional Earnings (+)</Label>
                  <Input
                    type="number"
                    value={adjustments.additional_earnings}
                    onChange={(e) => setAdjustments(prev => ({ ...prev, additional_earnings: e.target.value }))}
                    placeholder="0"
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted">Additional Deductions (-)</Label>
                  <Input
                    type="number"
                    value={adjustments.additional_deductions}
                    onChange={(e) => setAdjustments(prev => ({ ...prev, additional_deductions: e.target.value }))}
                    placeholder="0"
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-muted">Reason for Adjustment *</Label>
                  <Textarea
                    value={adjustments.adjustment_reason}
                    onChange={(e) => setAdjustments(prev => ({ ...prev, adjustment_reason: e.target.value }))}
                    placeholder="Explain the reason for this adjustment..."
                    className="bg-wt-bg border-wt-border text-wt-text-primary"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-wt-gold/10 border border-wt-gold/30">
                <p className="text-sm text-wt-text-muted">New Net Salary:</p>
                <p className="text-xl font-bold text-wt-gold">
                  {(
                    editingSlip.net_salary + 
                    (parseFloat(adjustments.additional_earnings) || 0) - 
                    (parseFloat(adjustments.additional_deductions) || 0)
                  ).toLocaleString()} AED
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAdjustments}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={processing}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Adjustments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollManagementPage;
